import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, XCircle, Edit3, AlertTriangle, RefreshCw, Ban } from 'lucide-react';
import { dashboardApi } from '../services/api';

/**
 * Module 6 — the review queue.
 *
 * Everything awaiting a human decision, oldest first. Every AI decision is
 * shown with its confidence and stated reason, and can be overridden; drafts
 * can be approved, edited before sending, or rejected.
 *
 * Styling follows the existing dashboard (Tailwind utility classes, the same
 * rounded-full status pills used in InfluencerList) — no redesign.
 */

const CATEGORIES = [
  'interested', 'question', 'not_interested',
  'out_of_office', 'unsubscribe', 'wrong_person'
];

function categoryClass(category) {
  switch (category) {
    case 'interested': return 'bg-green-100 text-green-800';
    case 'question': return 'bg-blue-100 text-blue-800';
    case 'out_of_office': return 'bg-yellow-100 text-yellow-800';
    case 'not_interested': return 'bg-gray-100 text-gray-800';
    case 'unsubscribe': return 'bg-red-100 text-red-800';
    case 'wrong_person': return 'bg-purple-100 text-purple-800';
    default: return 'bg-gray-100 text-gray-800';
  }
}

function confidenceClass(confidence) {
  if (confidence == null) return 'text-gray-400';
  if (confidence >= 0.8) return 'text-green-700';
  if (confidence >= 0.5) return 'text-yellow-700';
  return 'text-red-700';
}

const ReviewQueue = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [editing, setEditing] = useState({});   // conversationId -> draft body
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await dashboardApi.getReviewQueue(50);
      setItems(data.items || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const draftOf = (item) => {
    if (editing[item.conversation_id] !== undefined) return editing[item.conversation_id];
    try {
      const extracted = typeof item.extracted === 'string' ? JSON.parse(item.extracted) : item.extracted;
      return (extracted && extracted.draft && extracted.draft.body) || '';
    } catch (e) {
      return '';
    }
  };

  const act = async (id, fn) => {
    setBusyId(id);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusyId(null);
    }
  };

  const override = (item, category) => act(item.classification_id, () =>
    dashboardApi.overrideClassification(item.classification_id, { category, overridden_by: 'dashboard' }));

  const approve = (item) => act(item.conversation_id, () =>
    dashboardApi.approveDraft(item.conversation_id, {
      body: draftOf(item), approved_by: 'dashboard'
    }));

  const reject = (item) => act(item.conversation_id, () =>
    dashboardApi.rejectDraft(item.conversation_id, { rejected_by: 'dashboard' }));

  const suppress = (item) => act(item.influencer_id, () =>
    dashboardApi.suppressInfluencer(item.influencer_id, { notes: 'suppressed from review queue' }));

  if (loading) {
    return <div className="p-6 text-gray-500">Loading review queue…</div>;
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Review Queue</h1>
          <p className="text-sm text-gray-500">
            {items.length} item{items.length === 1 ? '' : 's'} awaiting a decision — oldest first
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {items.length === 0 && (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          Nothing awaiting review.
        </div>
      )}

      <div className="space-y-4">
        {items.map((item) => (
          <div key={item.classification_id} className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between">
              <div>
                <Link
                  to={`/influencer/${item.influencer_id}`}
                  className="text-blue-600 hover:text-blue-900 font-medium"
                >
                  @{item.username}
                </Link>
                <span className="ml-2 text-sm text-gray-500">
                  {item.full_name} · {Number(item.followers_count || 0).toLocaleString()} followers · {item.detected_country}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${categoryClass(item.category)}`}>
                  {item.category}
                </span>
                <span className={`text-xs font-medium ${confidenceClass(item.confidence)}`}>
                  {item.confidence != null ? `${(item.confidence * 100).toFixed(0)}% confident` : 'no confidence'}
                </span>
              </div>
            </div>

            {/* The AI's stated reasoning, always visible and explainable. */}
            {item.reason && (
              <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-start">
                <AlertTriangle className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Why:</span> {item.reason}
                  {item.model && <span className="text-gray-400"> · {item.model}</span>}
                </p>
              </div>
            )}

            {/* Their reply */}
            <div className="px-6 py-4">
              <p className="text-xs font-medium text-gray-500 uppercase mb-1">Their reply</p>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{item.body}</p>
            </div>

            {/* Draft, editable before sending */}
            {draftOf(item) && (
              <div className="px-6 pb-4">
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">
                  Draft reply — edit before approving
                </p>
                <textarea
                  className="w-full border border-gray-300 rounded-lg p-3 text-sm font-mono"
                  rows={7}
                  value={draftOf(item)}
                  onChange={(e) => setEditing({ ...editing, [item.conversation_id]: e.target.value })}
                />
              </div>
            )}

            <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex flex-wrap items-center gap-2">
              {draftOf(item) && (
                <>
                  <button
                    disabled={busyId === item.conversation_id}
                    onClick={() => approve(item)}
                    className="flex items-center px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    <CheckCircle className="w-4 h-4 mr-1.5" /> Approve &amp; send
                  </button>
                  <button
                    disabled={busyId === item.conversation_id}
                    onClick={() => reject(item)}
                    className="flex items-center px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    <XCircle className="w-4 h-4 mr-1.5" /> Reject
                  </button>
                </>
              )}

              <div className="flex items-center ml-auto">
                <Edit3 className="w-4 h-4 text-gray-400 mr-2" />
                <span className="text-sm text-gray-500 mr-2">Override:</span>
                <select
                  defaultValue=""
                  disabled={busyId === item.classification_id}
                  onChange={(e) => e.target.value && override(item, e.target.value)}
                  className="text-sm border border-gray-300 rounded-lg px-2 py-1.5"
                >
                  <option value="">choose…</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <button
                  onClick={() => suppress(item)}
                  className="ml-2 flex items-center px-3 py-1.5 text-sm text-red-700 border border-red-200 rounded-lg hover:bg-red-50"
                  title="Suppress permanently"
                >
                  <Ban className="w-4 h-4 mr-1.5" /> Suppress
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReviewQueue;
